import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { MessageSquare, Lock } from 'lucide-react';
import { useSavChats } from '../hooks/useSavChats';
import { SavChatList } from '../components/SavChatList';
import { AdminSavChat } from '../components/AdminSavChat';
import { SavChat } from '@/models/savChat';
import SeoTags from '@/components/shared/SeoTags';
import { isSavChatEnabled } from '@/shared/config/featureFlags';
import { useTranslation } from 'react-i18next';

const SavPage: React.FC = () => {
  const { t } = useTranslation();
  const { idorga } = useParams<{ idorga: string }>();
  const location = useLocation();
  const { chats, loading, stats, updateStatus } = useSavChats(idorga || null);
  const [selectedChat, setSelectedChat] = useState<SavChat | null>(null);

  // Vérifier si la fonctionnalité est activée pour cette organisation
  const savEnabled = isSavChatEnabled(idorga);

  // Présélectionner le chat si passé dans le state (depuis notifications)
  useEffect(() => {
    const state = location.state as { selectedChatId?: string } | null;
    if (state?.selectedChatId && chats.length > 0) {
      const chatToSelect = chats.find(c => c.id === state.selectedChatId);
      if (chatToSelect) {
        setSelectedChat(chatToSelect);
        // Nettoyer le state pour éviter de re-sélectionner si on change de chat manuellement puis re-render
        window.history.replaceState({}, document.title);
      }
    }
  }, [chats, location.state]);

  const handleSelectChat = (chat: SavChat) => {
    setSelectedChat(chat);
  };

  const handleUpdateStatus = async (chatId: string, status: any) => {
    await updateStatus(chatId, status);
  };

  // Si la fonctionnalité n'est pas activée, afficher un message ou rediriger
  if (!savEnabled) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-background p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-background-secondary rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="h-8 w-8 text-foreground-secondary" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-3">
            {t('admin.savChat.admin.notAvailable.title')}
          </h2>
          <p className="text-foreground-secondary mb-6">
            {t('admin.savChat.admin.notAvailable.description')}
          </p>
          <a
            href={`/orga/${idorga}/admin`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
          >
            {t('admin.savChat.admin.notAvailable.backToDashboard')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-background overflow-hidden">
      <SeoTags
        title={`${t('admin.savChat.admin.title')} | ${idorga || 'Admin'}`}
        description={t('admin.savChat.admin.selectChatDescription')}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        noIndex={true}
      />

      {/* Content */}
      <div className="flex-1 overflow-hidden min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-12 h-12 border-3 border-border border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-foreground-secondary">{t('admin.savChat.admin.loading')}</p>
            </div>
          </div>
        ) : (
          <div className="h-full flex overflow-hidden w-full">
            {/* Chat list - sidebar gauche - cachée sur mobile quand un chat est sélectionné */}
            <div className={`${selectedChat ? 'hidden md:flex' : 'flex'} w-full md:w-64 lg:w-72 flex-shrink-0 h-full border-r border-border overflow-hidden bg-surface`}>
              <SavChatList
                chats={chats}
                selectedChatId={selectedChat?.id || null}
                onSelectChat={handleSelectChat}
                stats={stats}
              />
            </div>

            {/* Chat content + Info sidebar - centre et droite - plein écran sur mobile */}
            <div className={`${selectedChat ? 'flex' : 'hidden md:flex'} flex-1 h-full w-full overflow-hidden min-w-0 bg-background`}>
              {selectedChat ? (
                <AdminSavChat
                  chatId={selectedChat.id}
                  organizationId={idorga || ''}
                  onUpdateStatus={handleUpdateStatus}
                  onBack={() => setSelectedChat(null)}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full w-full bg-background-secondary/30">
                  <MessageSquare className="h-16 w-16 text-foreground-secondary/50 mb-4" />
                  <p className="text-lg font-medium text-foreground">
                    {t('admin.savChat.admin.selectChat')}
                  </p>
                  <p className="text-sm text-foreground-secondary mt-1">
                    {t('admin.savChat.admin.selectChatDescription')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavPage;

